import React from 'react';

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'white' | 'secondary';
  className?: string;
}

export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className = ''
}) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  const colors = {
    primary: 'border-primary-600',
    white: 'border-white',
    secondary: 'border-secondary-600'
  };

  return (
    <div className={`${sizes[size]} ${colors[color]} border-2 border-t-transparent rounded-full animate-spin ${className}`} />
  );
};

interface LoadingOverlayProps {
  message?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ message }) => {
  return (
    <div className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl p-8 flex flex-col items-center gap-4 shadow-xl">
        <Spinner size="lg" />
        {message && <p className="text-secondary-600">{message}</p>}
      </div>
    </div>
  );
};

interface PageLoaderProps {
  message?: string;
}

export const PageLoader: React.FC<PageLoaderProps> = ({ message = 'Loading...' }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
      <Spinner size="lg" />
      <p className="text-secondary-500">{message}</p>
    </div>
  );
};

interface SkeletonProps {
  width?: string;
  height?: string;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '20px',
  rounded = 'md',
  className = ''
}) => {
  const roundedStyles = {
    none: '',
    sm: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full'
  };

  return (
    <div 
      className={`animate-pulse bg-secondary-200 ${roundedStyles[rounded]} ${className}`}
      style={{ width, height }}
    />
  );
};

export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  return (
    <div className="bg-white rounded-xl border border-secondary-100 overflow-hidden">
      <div className="h-12 bg-secondary-50 border-b border-secondary-200" />
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="h-16 border-b border-secondary-100 px-6 flex items-center gap-4">
          <Skeleton width="40px" height="40px" rounded="full" />
          <Skeleton width="200px" />
          <Skeleton width="150px" />
          <Skeleton width="80px" />
        </div>
      ))}
    </div>
  );
};

export const CardSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-xl border border-secondary-100 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton width="80px" height="16px" />
          <Skeleton width="120px" height="32px" className="mt-2" />
        </div>
        <Skeleton width="48px" height="48px" rounded="lg" />
      </div>
    </div>
  );
};
